import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import {
  EntitySelectEntity,
  Input,
  OneModal,
  Spinner,
} from '@factorialco/factorial-one-react/dist/experimental'
import {
  MeetingsAllMeetingsTableDocument,
  OneOnOneCreateScheduledMeetingDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EmployeeSelector from 'modules/meetings/components/EmployeeSelector'

export const CreateScheduledMeetingModal = ({
  openModal,
  setOpenModal,
}: {
  openModal: boolean
  setOpenModal: (open: boolean) => void
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const {
    employee: currentEmployee,
    access: { fullName: currentEmployeeFullName },
  } = useCurrentSession()

  const [isLoadingCreateMeeting, setIsLoadingCreateMeeting] = useState(false)
  const [employee, setEmployee] = useState<EntitySelectEntity | null>(null)

  const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
  const [startTime, setStartTime] = useState('10:00')
  const [endTime, setEndTime] = useState('11:00')

  const [createMeetingsMeeting] = useMutation(OneOnOneCreateScheduledMeetingDocument, {
    extractorFn: (data) => data.meetingsMutations.createMeetingsMeeting,
    refetchQueries: [MeetingsAllMeetingsTableDocument],
  })

  const canCreateMeeting = employee && date && startTime && endTime && startTime < endTime

  const onCreateMeeting = async () => {
    setIsLoadingCreateMeeting(true)

    if (canCreateMeeting) {
      const result = await createMeetingsMeeting({
        variables: {
          title: i18n.t('meetings.create_scheduled_meeting.titles.one_on_one', {
            employee_name: employee?.name || 'Employee',
            manager_name: currentEmployeeFullName || 'Manager',
          }),
          date: moment(date).format('YYYY-MM-DD'),
          startTime: moment(`${date} ${startTime}`).utc().toISOString(),
          endTime: moment(`${date} ${endTime}`).utc().toISOString(),
          description: i18n.t('meetings.create_scheduled_meeting.descriptions.scheduled_meeting'),
          attendeeIds: [Number(currentEmployee?.id), Number(employee?.id)],
        },
      })

      const handler = handleResult(result, () => null)

      if (handler.success) {
        const meeting = handler?.data?.meeting
        if (meeting?.id) {
          navigate(navigation.engagement.one_on_ones.meeting.meetId.fullPath({ id: meeting.id }))
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        }
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    }

    setIsLoadingCreateMeeting(false)
  }

  return (
    <OneModal
      isOpen={openModal}
      onClose={() => {
        if (!isLoadingCreateMeeting) {
          setOpenModal(false)
        }
      }}
    >
      {isLoadingCreateMeeting ? (
        <OneModal.Content>
          <Box
            paddingX="s16"
            paddingBottom="s64"
            paddingTop="s64"
            gap="s12"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner />
            <Text.Normal size="200">
              {i18n.t('meetings.create_scheduled_meeting.loading')}
            </Text.Normal>
          </Box>
        </OneModal.Content>
      ) : (
        <>
          <OneModal.Header title={i18n.t('meetings.create_scheduled_meeting.title')} />
          <OneModal.Content>
            <Box paddingX="s16" paddingBottom="s12" gap="s12">
              <Box gap="s8">
                <Text.Normal size="200">Employee</Text.Normal>
                <EmployeeSelector employee={employee} setEmployee={setEmployee} />
              </Box>

              <Box gap="s8">
                <Text.Normal size="200">
                  {i18n.t('meetings.create_scheduled_meeting.date')}
                </Text.Normal>
                <Input
                  placeholder={i18n.t('meetings.create_scheduled_meeting.date_placeholder')}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Box>

              <Box gap="s12" flexDirection="row">
                <Box gap="s8" flexGrow>
                  <Text.Normal size="200">
                    {i18n.t('meetings.create_scheduled_meeting.start_time')}
                  </Text.Normal>
                  <Input
                    placeholder={i18n.t('meetings.create_scheduled_meeting.start_time_placeholder')}
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Box>
                <Box gap="s8" flexGrow>
                  <Text.Normal size="200">
                    {i18n.t('meetings.create_scheduled_meeting.end_time')}
                  </Text.Normal>
                  <Input
                    placeholder={i18n.t('meetings.create_scheduled_meeting.end_time_placeholder')}
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          </OneModal.Content>
          <OneModal.Footer>
            <Button
              onClick={() => setOpenModal(false)}
              label={i18n.t('common.cancel')}
              variant="outline"
            />
            <Button
              disabled={!canCreateMeeting}
              onClick={onCreateMeeting}
              label={i18n.t('meetings.create_scheduled_meeting.create_meeting')}
              variant="default"
              isLoading={isLoadingCreateMeeting}
            />
          </OneModal.Footer>
        </>
      )}
    </OneModal>
  )
}
